import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import type { InvoiceLineItem } from "@/features/invoices/types";
import { centsToDollars, dollarsToCents, formatCents } from "@/lib/utils";

interface LineItemRowProps {
  item: InvoiceLineItem;
  onUpdate: (patch: Partial<Pick<InvoiceLineItem, "description" | "quantity" | "unitPriceCents">>) => void;
  onDelete: () => void;
}

/**
 * Editable line-item row for a DRAFT invoice. Render with `key={item.id}` so
 * this remounts (resetting its local unit-price text) whenever the item
 * identity changes, instead of syncing local state to props via an effect.
 */
export function LineItemRow({ item, onUpdate, onDelete }: LineItemRowProps) {
  const [unitPriceDollars, setUnitPriceDollars] = useState(() => centsToDollars(item.unitPriceCents));

  return (
    <TableRow>
      <TableCell>
        <Input
          value={item.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="e.g. IRS transcript analysis & tax relief report"
          className="border-transparent bg-transparent shadow-none"
        />
      </TableCell>
      <TableCell className="w-24">
        <Input
          type="number"
          min={0}
          step="any"
          value={item.quantity}
          onChange={(e) => onUpdate({ quantity: Number(e.target.value) || 0 })}
          className="border-transparent bg-transparent text-right shadow-none"
        />
      </TableCell>
      <TableCell className="w-32">
        <Input
          inputMode="decimal"
          placeholder="0.00"
          value={unitPriceDollars}
          onChange={(e) => setUnitPriceDollars(e.target.value)}
          onBlur={() => onUpdate({ unitPriceCents: dollarsToCents(unitPriceDollars) })}
          className="border-transparent bg-transparent text-right shadow-none"
        />
      </TableCell>
      <TableCell className="text-right font-medium">{formatCents(item.extendedAmtCents)}</TableCell>
      <TableCell className="w-10">
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Delete line item" onClick={onDelete}>
          <Trash2 className="text-destructive size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
